import { Copy } from "lucide-react";
import { Modal } from "../Modal";
import { ActionButton, GhostButton, FormField, inputClass } from "../Primitives";
import { MultiSelectDropdown } from "../MultiSelectDropdown";
import { WEEKDAY_LABELS } from "./shiftUtils";
export function EditSeriesModal({
  form,
  setForm,
  sites,
  guardOptions,
  onClose,
  onSave,
  onDuplicate,
  submitting = false,
  submittingDuplicate = false,
}) {
  const days = Array.isArray(form.recurrence_days) ? form.recurrence_days : [];
  const toggleDay = (d) => {
    const nextSet = new Set(days.map((n) => Number(n)));
    if (nextSet.has(d)) nextSet.delete(d);
    else nextSet.add(d);
    setForm({ ...form, recurrence_days: [...nextSet].sort() });
  };
  return (
    <Modal title="Edit Recurring Series" onClose={onClose} testId="edit-series-modal">
      <div className="text-xs text-zinc-500 mb-4 border-l-2 border-[#FF9F0A] pl-3">
        Changes apply to <strong>all shifts</strong> in this series. Change the <em>days of the week</em>, <em>first date</em>, or <em>until date</em> to reshape the schedule — future shifts that no longer match will be removed and any missing ones will be added.
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <FormField label="Series label (optional)">
            <input
              type="text"
              data-testid="series-label-input"
              className={inputClass}
              placeholder="e.g. Q1 patrol · Acme HQ"
              value={form.series_label || ""}
              onChange={(e) => setForm({ ...form, series_label: e.target.value })}
              maxLength={80}
            />
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
              Shown on every shift card in this series. Leave blank to clear.
            </div>
          </FormField>
        </div>
        <div className="col-span-2">
          <FormField label="Officers (one or more)">
            <MultiSelectDropdown
              testId="series-guards"
              label="Select "
              options={guardOptions}
              selected={form.guard_ids}
              onChange={(ids) => setForm({ ...form, guard_ids: ids })}
              searchable
              triggerClassName="w-full"
              emptyHint="No  match"
            />
          </FormField>
        </div>
        {(() => {
          // iter326 — Show the guard-change scoping picker only when the
          // caller has actually swapped or added a guard. Comparing sets
          // lets us ignore reorderings.
          const orig = new Set(form.original_guard_ids || []);
          const cur = new Set(form.guard_ids || []);
          const changed = orig.size !== cur.size || [...cur].some((g) => !orig.has(g));
          if (!changed) return null;
          const mode = form.guard_change_mode || "replace_all";
          return (
            <div className="col-span-2" data-testid="guard-change-mode-picker">
              <FormField label="Guard change scope">
                <div className="grid gap-2">
                  {[
                    { key: "replace_all", title: "Replace entire series", desc: "Swap the officer on every non-completed shift." },
                    { key: "replace_from", title: "Replace going forward from…", desc: "Keep past assignments; swap only from the chosen date onwards." },
                    { key: "add_from", title: "Add as additional guard from…", desc: "Layer the new officer on top; keep existing ones." },
                  ].map((opt) => {
                    const on = mode === opt.key;
                    return (
                      <button
                        type="button"
                        key={opt.key}
                        data-testid={`guard-change-mode-${opt.key}`}
                        onClick={() => setForm({ ...form, guard_change_mode: opt.key })}
                        className={`text-left px-3 py-2 border transition-colors ${
                          on ? "border-[#FF3B30] bg-[#FF3B30]/10" : "border-[#27272A] hover:border-[#FF3B30]/60"
                        }`}
                      >
                        <div className={`text-xs font-bold uppercase tracking-widest ${on ? "text-[#FF3B30]" : "text-zinc-300"}`}>
                          {opt.title}
                        </div>
                        <div className="text-[10px] text-zinc-500 mt-0.5">{opt.desc}</div>
                      </button>
                    );
                  })}
                </div>
                {mode !== "replace_all" && (
                  <div className="mt-2">
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500">From date</label>
                    <input
                      type="date"
                      data-testid="guard-change-from-date"
                      className={inputClass}
                      min={form.first_date}
                      max={form.new_end_date}
                      value={form.guard_change_from || ""}
                      onChange={(e) => setForm({ ...form, guard_change_from: e.target.value })}
                    />
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                      Interpreted in the site&rsquo;s timezone. Completed shifts on/after this date are still left alone.
                    </div>
                  </div>
                )}
                {/* iter328 — Weekday filter: narrow the change to only
                    specific days-of-week (e.g. Fridays only). Empty = all
                    days in the series. Chips are dimmed for weekdays not
                    part of the series pattern so ops don't tag a day the
                    series never runs on. */}
                <div className="mt-3">
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500">Only on these days (optional)</label>
                  <div className="flex flex-wrap gap-1.5 mt-1" data-testid="guard-change-days-chips">
                    {WEEKDAY_LABELS.map((lbl, idx) => {
                      const seriesDays = Array.isArray(form.recurrence_days) ? form.recurrence_days : [];
                      const inSeries = seriesDays.length === 0 || seriesDays.includes(idx);
                      const sel = Array.isArray(form.guard_change_days) && form.guard_change_days.includes(idx);
                      return (
                        <button
                          type="button"
                          key={idx}
                          disabled={!inSeries}
                          data-testid={`guard-change-day-${idx}`}
                          onClick={() => {
                            const cur = new Set(form.guard_change_days || []);
                            if (cur.has(idx)) cur.delete(idx); else cur.add(idx);
                            setForm({ ...form, guard_change_days: [...cur].sort() });
                          }}
                          className={`px-2 py-1 text-[10px] uppercase tracking-widest border transition-colors duration-150 ${
                            !inSeries
                              ? "border-[#27272A] text-zinc-700 cursor-not-allowed"
                              : sel
                              ? "bg-[#FF3B30] border-[#FF3B30] text-white"
                              : "border-[#27272A] text-zinc-400 hover:border-[#FF3B30]"
                          }`}
                        >
                          {lbl}
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                    Leave empty to apply on every day the series runs. Example: pick <span className="text-[#FF3B30]">FRI</span> to change the officer for Fridays only.
                  </div>
                </div>
              </FormField>
            </div>
          );
        })()}
        <FormField label="Site">
          <select data-testid="series-site-select" className={inputClass} value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })}>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </FormField>
        <div></div>
        <FormField label="Start Time">
          <input type="time" data-testid="series-start-hhmm" className={inputClass} value={form.start_hhmm} onChange={(e) => setForm({ ...form, start_hhmm: e.target.value })} />
        </FormField>
        <FormField label="End Time">
          <input type="time" data-testid="series-end-hhmm" className={inputClass} value={form.end_hhmm} onChange={(e) => setForm({ ...form, end_hhmm: e.target.value })} />
        </FormField>
        {/* iter288 — clarify which zone the operator is editing in.
            Shift times persist as UTC internally but the roster and
            these inputs both surface site-local wall-clock time. */}
        <div className="col-span-2 -mt-2 text-[10px] uppercase tracking-widest text-zinc-500" data-testid="series-time-tz-hint">
          {form.site_timezone
            ? <>Times shown in <span className="text-[#0A84FF] font-mono">{form.site_timezone}</span> (site local time).</>
            : <>Times shown in your browser&rsquo;s local time — <span className="text-[#FF9F0A]">set the site&rsquo;s timezone in Site Settings for accurate scheduling</span>.</>}
        </div>
        <FormField label="Minimum Officers Required">
          <input
            type="number"
            min="0"
            max="20"
            data-testid="series-min-guards"
            className={inputClass}
            value={form.min_guards}
            onChange={(e) => setForm({ ...form, min_guards: e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value, 10) || 0) })}
          />
        </FormField>
        <div></div>
        <div className="col-span-2">
          <FormField label="Days of the week">
            <div className="flex flex-wrap gap-1.5" data-testid="series-recurrence-days">
              {WEEKDAY_LABELS.map((lbl, idx) => {
                const on = days.includes(idx);
                return (
                  <button
                    type="button"
                    key={idx}
                    data-testid={`series-day-chip-${idx}`}
                    onClick={() => toggleDay(idx)}
                    className={`px-2 py-1 text-[10px] uppercase tracking-widest border transition-colors duration-150 ${
                      on
                        ? "bg-[#FF3B30] border-[#FF3B30] text-white"
                        : "border-[#27272A] text-zinc-400 hover:border-[#FF3B30]"
                    }`}
                  >
                    {lbl}
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
              Uncheck a day to drop those shifts; check a new day to schedule it. Completed shifts are never touched.
            </div>
          </FormField>
        </div>
        <FormField label="First date (series start)">
          <input
            type="date"
            data-testid="series-start-date"
            className={inputClass}
            value={form.new_start_date || form.first_date || ""}
            onChange={(e) => setForm({ ...form, new_start_date: e.target.value })}
          />
        </FormField>
        <FormField label="Until (last date)">
          <input
            type="date"
            data-testid="series-end-date"
            className={inputClass}
            min={form.new_start_date || form.first_date}
            value={form.new_end_date}
            onChange={(e) => setForm({ ...form, new_end_date: e.target.value })}
          />
        </FormField>
        <div className="col-span-2">
          <FormField label="Notes">
            <textarea rows={2} className={inputClass} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </FormField>
        </div>
      </div>
      <div className="mt-6 flex justify-between items-center gap-2">
        <button
          data-testid="duplicate-series-button"
          onClick={onDuplicate}
          disabled={submittingDuplicate || submitting}
          className="text-xs uppercase tracking-widest text-zinc-500 hover:text-[#0A84FF] inline-flex items-center gap-1 disabled:opacity-40 disabled:pointer-events-none"
        >
          <Copy className="size-3" /> {submittingDuplicate ? "Copying…" : "Duplicate Series"}
        </button>
        <div className="flex gap-2">
          <GhostButton onClick={onClose} disabled={submitting}>Cancel</GhostButton>
          <ActionButton
            data-testid="save-series-button"
            onClick={onSave}
            disabled={submitting || submittingDuplicate}
          >
            {submitting ? "Updating…" : "Update Series"}
          </ActionButton>
        </div>
      </div>
    </Modal>
  );
}
